// Lists a class's exams without reading an attempt: the course units name the exams, a roster count per exam
// says who sat it, and attempts (about 21 KB each) wait until a row is opened. Import-free, for node --test.

import type { Id, IztContentNode, IztExamSubmission } from '../../api/types';
import { contentKind } from '../../config/content-types.ts';
import { studentEmailDomainUnset } from '../submissions/submissions.ts';
import { examTitlesFromUnits } from './examResults.ts';
import { type MaybeMsg } from '../../i18n/msg.ts';

export const EXAM_CONCURRENCY = 6;

// A sentinel, not text: the screen shows exams:errors.izSignedOut in the teacher's language.
export const IZ_SIGNED_OUT = 'iz-signed-out';

/** Why a load stops before any request: an error to show, or `{ error: null }` for an empty roster; null to go on. */
export function examLoadBlock(domain: string, pattern: string, hasToken: boolean): { error: MaybeMsg | null } | null {
  if (!domain) return { error: studentEmailDomainUnset() };
  if (!pattern) return { error: null };
  // unit reads need the IZ token, and without them there is no fast way to name the class's exams
  if (!hasToken) return { error: IZ_SIGNED_OUT };
  return null;
}

/** One class's exam load; a token arriving after mount changes it, so the load runs again. */
export function examLoadKey(input: { codes: string[]; classId: Id; gradeId?: Id; subjectId?: Id }, signedIn: boolean): string {
  return [input.classId, input.gradeId || '', input.subjectId || '', signedIn ? 'iz' : '', input.codes.join(',')].join('|');
}

export interface ExamUnit {
  id?: string;
  _id?: string;
  title?: string | null;
  type?: string | null;
  exam?: string | null;
}

/** One test unit's exam, in tree order; the list shows it once its count lands. */
export interface ClassExamRef {
  examId: string;
  courseId: string;
}

export interface ClassExamList {
  exams: ClassExamRef[];
  /** exam id to its unit's title. */
  examTitles: Record<string, string>;
  /** IZ course id to its name, from the tree. */
  courseTitles: Record<string, string>;
  /** exam id to the test unit wrapping it, so the card can open that unit on the board. */
  examUnits: Record<string, ExamUnitRef>;
}

/** Roster students with a graded attempt, and attempts still in progress. */
export interface ExamCount {
  submitted: number;
  doing: number;
}

export interface ExamUnitRef {
  unitId: string;
  courseId: string;
  title: string;
}

/** First test unit per exam, by course. */
export function examUnitsFrom(courseIds: string[], unitLists: ExamUnit[][]): Record<string, ExamUnitRef> {
  const out: Record<string, ExamUnitRef> = {};
  unitLists.forEach((units, i) => units.forEach((unit) => {
    const exam = unit.exam ? String(unit.exam) : '';
    const unitId = String(unit.id || unit._id || '');
    if (!exam || !unitId || out[exam] || contentKind(unit.type || null) !== 'assessment') return;
    out[exam] = { unitId, courseId: courseIds[i], title: String(unit.title || '').trim() };
  }));
  return out;
}

/** Tree courses holding at least one test unit; the others cannot have an exam to read. */
export function assessmentCourses(tree: IztContentNode[]): IztContentNode[] {
  const seen: Record<string, boolean> = {};
  return tree.filter((course) => {
    if (!course.id || seen[course.id]) return false;
    seen[course.id] = true;
    return course.sections.some((section) => section.units.some((unit) => contentKind(unit.type) === 'assessment'));
  });
}

/** Exam ids of the test units, first seen first, without repeats. */
export function examIdsFromUnits(units: ExamUnit[]): string[] {
  const ids: string[] = [];
  units.forEach((unit) => {
    const exam = unit.exam ? String(unit.exam) : '';
    if (exam && contentKind(unit.type || null) === 'assessment' && ids.indexOf(exam) < 0) ids.push(exam);
  });
  return ids;
}

/** Rows whose email matches the anchored roster pattern, case-insensitive like the service. */
export function rosterRows(rows: IztExamSubmission[], pattern: string): IztExamSubmission[] {
  if (!pattern) return [];
  const matcher = new RegExp(pattern, 'i');
  return rows.filter((row) => matcher.test(String(row.email || '').trim()));
}

/** `run` over `items` with at most `limit` in flight; results keep the input order, the first failure rejects. */
export async function mapLimit<T, R>(items: T[], limit: number, run: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  async function worker(): Promise<void> {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await run(items[index]);
    }
  }
  const workers: Promise<void>[] = [];
  for (let i = 0; i < Math.min(Math.max(1, limit), items.length); i += 1) workers.push(worker());
  await Promise.all(workers);
  return results;
}

/** The course units only (one read per course with a test unit); no attempt is read here. */
export async function loadClassExamList(unitsOf: (courseId: string) => Promise<ExamUnit[]>, tree: IztContentNode[], concurrency: number = EXAM_CONCURRENCY): Promise<ClassExamList> {
  const courses = assessmentCourses(tree);
  const courseTitles: Record<string, string> = {};
  courses.forEach((course) => { if (course.name) courseTitles[course.id] = course.name; });
  const unitLists = await mapLimit(courses, concurrency, (course) => unitsOf(course.id));
  const units = unitLists.reduce<ExamUnit[]>((all, list) => all.concat(list), []);
  const examTitles = examTitlesFromUnits(units.filter((unit) => contentKind(unit.type || null) === 'assessment'));
  const examUnits = examUnitsFrom(courses.map((course) => course.id), unitLists);
  // an exam with no unit id has no đề to open, so it is not listed
  const exams = examIdsFromUnits(units).filter((examId) => examUnits[examId]).map((examId) => ({ examId, courseId: examUnits[examId].courseId }));
  return { exams, examTitles, courseTitles, examUnits };
}

export const SUBMITTED_STATES = ['final'];
export const DOING_STATES = ['started', 'doing'];

/** Two counts per exam; a resubmission turns the older attempt 'done', so 'final' is one per student. */
export async function examCount(countOf: (examId: string, states: string[]) => Promise<number>, examId: string): Promise<ExamCount> {
  const counts = await Promise.all([countOf(examId, SUBMITTED_STATES), countOf(examId, DOING_STATES)]);
  return { submitted: counts[0], doing: counts[1] };
}

/** Exams the class has sat, in tree order: a counted exam with no attempt drops, one not yet counted waits. */
export function countedExams(exams: ClassExamRef[], counts: Record<string, ExamCount>): Array<ClassExamRef & ExamCount> {
  const out: Array<ClassExamRef & ExamCount> = [];
  exams.forEach((exam) => {
    const count = counts[exam.examId];
    if (count && count.submitted + count.doing > 0) out.push({ ...exam, submitted: count.submitted, doing: count.doing });
  });
  return out;
}
